import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { GRAY_700, GRAY_300 } from 'constants/styling/theme'
import { SignedURLDTO } from 'models/visuals'

import { DetailBooleanField, DetailCommentBlock, DetailField, DetailFileAttachment, DetailSection } from '../../common'

interface FloorPlanConfig {
  style: string
  orientation3d?: string
  fileOrientation?: string[]
  fixtures: boolean
  furniture: boolean
  gardensAndRoofSlopes?: boolean
  roomNames: boolean
  showDimensionChain: boolean
  showEachRoomArea: boolean
  scaleBar: boolean
  compass: boolean
  outputFileFormat: string[]
  outputFileLanguage?: string
  companyLogoOriginalName?: string
  companyLogoSignedUrl?: SignedURLDTO
  disclaimer?: string
  isMultiStoryPlan?: boolean
}

interface Props {
  config: FloorPlanConfig
}

/**
 * Shared floor plan configuration display used by Floor Plan, Living Area Calc,
 * and Virtual Tour renderers.
 *
 * Renders Design, Layout, Format sections and Output Language field.
 */
export const FloorPlanConfigSection: FC<Props> = ({ config }) => {
  const { t } = useTranslation(['gallery', 'purchase_flow', 'language', 'enums'])

  const styleLabel = useMemo(() => {
    if (!config.style) return undefined
    const fromStyles = t(`purchase_flow:floor_plan_step.design.styles.${config.style}`, { defaultValue: '' })
    if (fromStyles) return fromStyles
    const fromColors = t(`purchase_flow:floor_plan_step.design.colors.${config.style}`, { defaultValue: '' })
    if (fromColors) return fromColors
    return config.style
  }, [config.style, t])

  const orientationLabel = useMemo(() => {
    if (!config.orientation3d) return undefined
    return t(`purchase_flow:floor_plan_step.design.orientations.${config.orientation3d.toLowerCase()}`, { defaultValue: config.orientation3d })
  }, [config.orientation3d, t])

  return (
    <>
      <DetailSection title={t('gallery:assignment_details.floor_plan.design.title')}>
        <DetailField
          label={t('gallery:assignment_details.floor_plan.design.style')}
          value={styleLabel}
        />

        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.design.fixtures')}
          value={config.fixtures}
        />

        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.design.furnitures')}
          value={config.furniture}
        />

        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.design.gardens_and_roof_slopes')}
          value={config.gardensAndRoofSlopes}
        />
      </DetailSection>

      <DetailSection title={t('gallery:assignment_details.floor_plan.layout.title')}>
        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.layout.room_names')}
          value={config.roomNames}
        />

        <DetailField
          label={t('gallery:assignment_details.floor_plan.layout.dimensions')}
          contentSx={{
            'border': '0.1rem solid ' + GRAY_300,
            'border-radius': '0.4rem',
            'padding': '1.6rem 1.6rem 0 1.6rem',
          }}
        >
          <DetailBooleanField
            label={t('gallery:assignment_details.floor_plan.layout.dimension_strings')}
            value={config.showDimensionChain}
          />

          <DetailBooleanField
            label={t('gallery:assignment_details.floor_plan.layout.each_room_area')}
            value={config.showEachRoomArea}
          />
        </DetailField>

        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.layout.scalebar')}
          value={config.scaleBar}
        />

        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.layout.compass')}
          value={config.compass}
        />

        {config?.companyLogoOriginalName && config?.companyLogoSignedUrl &&
          <DetailField label={t('gallery:assignment_details.floor_plan.company_logo')}>
            <DetailFileAttachment
              fileName={config.companyLogoOriginalName}
              onDownload={() => config.companyLogoSignedUrl!.signedURL.startsWith('https://') && window.open(config.companyLogoSignedUrl!.signedURL, '_blank')}
              icon={<ImageOutlinedIcon sx={{ color: GRAY_700, fontSize: '2rem' }} />}
            />
          </DetailField>
        }

        <DetailField label={t('gallery:assignment_details.floor_plan.disclaimer')}>
          <DetailCommentBlock>{config.disclaimer ?? '-'}</DetailCommentBlock>
        </DetailField>

        <DetailBooleanField
          label={t('gallery:assignment_details.floor_plan.multi_story')}
          value={config.isMultiStoryPlan}
        />
      </DetailSection>

      <DetailSection title={t('gallery:assignment_details.floor_plan.format.title')}>
        <DetailField
          label={t('gallery:assignment_details.floor_plan.format.output_format')}
          value={config.outputFileFormat?.join(', ')}
        />

        <DetailField
          label={t('gallery:assignment_details.floor_plan.format.orientation')}
          value={orientationLabel}
        />

        {!!config.fileOrientation?.length &&
          <DetailField
            label={t('gallery:assignment_details.floor_plan.format.file_orientation')}
            value={config.fileOrientation.map(o => t(`enums:floor_plan_orientation.${o}`, { defaultValue: o })).join(', ')}
          />
        }
      </DetailSection>
    </>
  )
}
